import { createFrontendModule } from '@backstage/frontend-plugin-api';
import { EntityCardBlueprint } from '@backstage/plugin-catalog-react/alpha';
import catalogPlugin from '@backstage/plugin-catalog/alpha';
import { EntityLinksCard } from '@backstage/plugin-catalog';
import { EntityAboutCard } from './EntityAboutCard';
import { EntityOwnershipCard, showsOwnership } from './EntityOwnershipCard';
import {
  EntityRelationsCard,
  hasRenderableRelations,
} from './EntityRelationsCard';
import { DomainSystemsCard, isDomain } from './DomainSystemsCard';
import { SystemComponentsCard, isSystem } from './SystemComponentsCard';
import { LinkRows } from './LinkRows';
import { PhiTouchingCard, showsPhiScope } from './phiTouching';

/*
 * PHI scope, in the rail under Ownership. It was a full-width banner across the
 * top of the overview; a one-line fact does not need the width of the page.
 */
const phiScopeCard = EntityCardBlueprint.make({
  name: 'phi-scope',
  params: {
    type: 'info',
    filter: showsPhiScope,
    loader: async () => <PhiTouchingCard />,
  },
});

/*
 * About carries only a description and tags — the mock's card. Everything else
 * the vendor card held now reads somewhere else on the page, so it was a second
 * copy of the rail. Overridden through `params`: the vendor's `iconLinks`
 * extension input goes with it, which is the VIEW SOURCE / VIEW TECHDOCS row
 * the mock does not have.
 */
const aboutCard = catalogPlugin
  .getExtension('entity-card:catalog/about')
  .override({
    params: {
      loader: async () => <EntityAboutCard />,
    },
  });

/* Who owns the entity, in the rail rather than the page header. */
const ownershipCard = EntityCardBlueprint.make({
  name: 'entity-ownership',
  params: {
    type: 'info',
    filter: showsOwnership,
    loader: async () => <EntityOwnershipCard />,
  },
});

/*
 * Relations render as ONE compact card in the rail, with a labelled chip row per
 * relation type — the mock's treatment. It replaces the full-width Components,
 * Provided APIs and Consumed APIs tables that used to run down the main column,
 * three cards deep, to say what the chips now say in a few lines.
 */
const relationsCard = EntityCardBlueprint.make({
  name: 'entity-relations',
  params: {
    type: 'info',
    filter: hasRenderableRelations,
    loader: async () => <EntityRelationsCard />,
  },
});

/*
 * The Links card lays its links out as a responsive `Grid` of icon-over-label
 * boxes, which in the rail folds each label under its own icon. Re-skinned to
 * the mock's Links rows; see `LinkRows`.
 */
const linksCard = catalogPlugin
  .getExtension('entity-card:catalog/links')
  .override({
    params: {
      loader: async () => (
        <LinkRows>
          <EntityLinksCard />
        </LinkRows>
      ),
    },
  });

/*
 * A Domain has no readme or code cards, so its overview was only the empty
 * panel. The Systems it groups fill the main column instead.
 */
const domainSystemsCard = EntityCardBlueprint.make({
  name: 'domain-systems',
  params: {
    type: 'content',
    filter: isDomain,
    loader: async () => <DomainSystemsCard />,
  },
});

/*
 * A System has no readme or code cards of its own, so its overview was only
 * the empty panel. The components, APIs and resources it groups fill the main
 * column instead.
 */
const systemComponentsCard = EntityCardBlueprint.make({
  name: 'system-components',
  params: {
    type: 'content',
    filter: isSystem,
    loader: async () => <SystemComponentsCard />,
  },
});

export const catalogCardsModule = createFrontendModule({
  pluginId: 'catalog',
  extensions: [
    aboutCard,
    ownershipCard,
    phiScopeCard,
    relationsCard,
    linksCard,
    domainSystemsCard,
    systemComponentsCard,
  ],
});
